import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { EnvelopeOpenIcon } from '@heroicons/react/24/outline';
import { candidateSignup } from '../../utils/candidateApi';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { formatCandidateSignupError } from '../../utils/candidateAuthErrors';
import { applyServerFieldErrors } from '../../utils/errors';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function CandidateSignup() {
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [sentTo, setSentTo] = useState(null);
  const { register, handleSubmit, setError, formState: { errors } } = useForm();

  // Signup no longer returns a session: the server emails a one-time link and
  // the password is set on /candidate/verify-signup.
  const onSubmit = async ({ email, app_code }) => {
    setLoading(true);
    setFormError(null);
    const normalizedEmail = email.trim();
    try {
      await candidateSignup({
        email: normalizedEmail,
        app_code: app_code.trim().toUpperCase(),
      });
      setSentTo(normalizedEmail);
    } catch (err) {
      const applied = applyServerFieldErrors(err, setError, { fields: ['email', 'app_code'] });
      if (!applied.length) setFormError(formatCandidateSignupError(err));
    } finally {
      setLoading(false);
    }
  };

  if (sentTo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-8" data-testid="candidate-signup-page">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm border p-8 text-center" data-testid="candidate-signup-sent">
          <div className="h-14 w-14 rounded-2xl bg-brand-50 border border-brand-100 flex items-center justify-center mx-auto mb-4">
            <EnvelopeOpenIcon className="h-7 w-7 text-brand-700" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900">Check your email</h1>
          <p className="text-sm text-gray-600 mt-2">
            If <span className="font-medium">{sentTo}</span> matches an application, we have sent a link to
            finish setting up your portal account. The link works once and expires shortly.
          </p>
          <p className="text-xs text-gray-500 mt-4">
            Nothing arrived? Check your spam folder, confirm you used the exact email and HIQ- code from your
            application, then try again.
          </p>
          <div className="mt-6 flex flex-col gap-3">
            <Link to="/candidate/login">
              <Button className="w-full">Go to sign in</Button>
            </Link>
            <Button variant="secondary" onClick={() => setSentTo(null)}>Use a different email or code</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-8" data-testid="candidate-signup-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Candidate Sign Up</h1>
          <p className="text-gray-500 mt-2">Create your portal account using your application code</p>
        </div>

        {formError && (
          <div
            role="alert"
            data-testid="candidate-signup-error"
            className="mb-4 rounded-xl border border-danger-200 bg-danger-50 px-4 py-3.5"
          >
            <p className="text-sm font-semibold text-danger-800">We couldn&apos;t start your sign up</p>
            <p className="mt-1 text-sm text-danger-700">{formError}</p>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-xl shadow-sm border p-8 space-y-4" data-testid="candidate-signup-form">
          <Input
            label="Email"
            type="email"
            autoFocus
            autoComplete="email"
            data-testid="candidate-signup-email"
            error={errors.email?.message}
            {...register('email', {
              required: 'Enter the email you applied with',
              pattern: { value: EMAIL_PATTERN, message: 'Enter a valid email address' },
            })}
          />
          <Input
            label="Application code"
            placeholder="HIQ-XXXXXX"
            autoComplete="off"
            data-testid="candidate-signup-code"
            error={errors.app_code?.message}
            {...register('app_code', {
              required: 'Enter the HIQ- code from your application',
              pattern: {
                value: /^HIQ-[A-Z0-9]{6}$/i,
                message: 'Use the format HIQ-XXXXXX from your confirmation email',
              },
            })}
          />
          <p className="text-xs text-gray-500 -mt-2">
            Must match the email and HIQ- code from your application exactly. We will email you a link to set
            your password.
          </p>
          <Button type="submit" className="w-full" loading={loading} data-testid="candidate-signup-submit">
            {loading ? 'Sending link...' : 'Email me a sign-up link'}
          </Button>
          <p className="text-center text-sm text-gray-500">
            Already have an account?{' '}
            <Link to="/candidate/login" className="text-brand-600 font-medium hover:underline">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
